import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { FolderOpen, FileText, Cpu, TrendingUp, Plus } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { usePlanLimits } from '@/hooks/usePlanLimits'
import { projectsApi } from '@/api/projects'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatDate, formatCredits, statusColor } from '@/lib/utils'

export default function Dashboard() {
  const { user } = useAuth()
  const { balanceCredits, currentPlan, subscription } = usePlanLimits()

  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: projectsApi.list,
  })

  const recentProjects = projects.slice(0, 5)
  const totalDocs = projects.reduce((sum, p) => sum + (p.document_count || 0), 0)

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Welcome back, {user?.full_name?.split(' ')[0] || 'there'}!</h1>
        <p className="text-muted-foreground mt-1">
          Here&apos;s an overview of your document intelligence platform.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Projects</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{projects.length}</div>
            <p className="text-xs text-muted-foreground">
              {currentPlan?.max_projects ? `of ${currentPlan.max_projects} allowed` : 'unlimited'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Documents</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalDocs}</div>
            <p className="text-xs text-muted-foreground">across all projects</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Credits</CardTitle>
            <Cpu className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{balanceCredits.toFixed(1)}</div>
            <p className="text-xs text-muted-foreground">
              <Link to="/wallet" className="text-primary hover:underline">Top up wallet</Link>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Plan</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{currentPlan?.name || 'Free'}</div>
            <p className="text-xs text-muted-foreground">
              <Link to="/billing" className="text-primary hover:underline">Manage subscription</Link>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Projects */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Recent Projects</h2>
          <Button asChild size="sm">
            <Link to="/projects/new">
              <Plus className="h-4 w-4 mr-1" />
              New Project
            </Link>
          </Button>
        </div>

        {recentProjects.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No projects yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first project to start extracting data from documents.
              </p>
              <Button asChild>
                <Link to="/projects/new">Create Project</Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {recentProjects.map((project) => (
              <Card key={project.id} className="hover:border-primary/50 transition-colors">
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <FolderOpen className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-medium hover:text-primary"
                      >
                        {project.name}
                      </Link>
                      <p className="text-sm text-muted-foreground">
                        {project.document_count} docs · {project.field_count} fields
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    <div>
                      <p className="text-xs text-muted-foreground">Created</p>
                      <p className="text-sm">{formatDate(project.created_at)}</p>
                    </div>
                    {project.is_archived && (
                      <Badge variant="secondary">Archived</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
            {projects.length > 5 && (
              <Link to="/projects" className="text-sm text-primary hover:underline text-center block">
                View all {projects.length} projects
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
